import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectEffectiveDate,
  selectCompTypes,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateManufacturedGLAAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatio) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const { gla, craftsmanQuality: quality } = subjectImprovements;
  const { gla: compGla } = compImprovements;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (
    isUndefined(gla) ||
    isUndefined(compGla) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  return adjCalculator.getManufacturedGLAAdj({
    subject: { quality, sqft: AloftArea.schema.parse(gla) },
    comp: { sqft: AloftArea.schema.parse(compGla) },
    depreciationRatio,
  }).adjustment;
};

const selectManufacturedGLAAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (_state, id: string) => id,
    selectCompTypes,
  ],
  calculateManufacturedGLAAdj,
);

export const selectManufacturedGLAAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentProps, improvements, depreciationRatio, effectiveDate, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      if (improvements[propID]?.propertyType !== 'manufacturedHome') {
        return acc;
      }
      const adj = calculateManufacturedGLAAdj(
        adjustmentProps,
        improvements,
        depreciationRatio,
        effectiveDate,
        propID,
        compTypes,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedManufacturedGLAAdj = createPriceSelector(
  [selectManufacturedGLAAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'mainDwelling', adjSettings);
  },
);

export const selectAppliedManufacturedGLAAdjDisplay = createSelector(
  [selectAppliedManufacturedGLAAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);
